import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import { verifyAddressViaPlaces, decideVerification, dedupAddresses } from "../places";
import { getPropertyInfo, type PropertyInfoDto } from "../api";
import type { PropertyHeader } from "./types";

interface UsePropertyInfoArgs {
  /** True while the review step is active — address resolution only runs then. */
  active: boolean;
  data: PropertyHeader | null | undefined;
  /** Merge a partial into the extraction data (reducer dispatch or setState). */
  patch: (p: Partial<PropertyHeader>) => void;
  googleMapsApiKey: string | null | undefined;
}

export interface UsePropertyInfoResult {
  savedInfo: PropertyInfoDto | null;
  savedInfoSearching: boolean;
  addressVerifying: boolean;
  applySavedInfo: () => void;
  /** Call when a NEW extraction's data arrives so verification re-runs. */
  reset: () => void;
}

// The three review-page address effects shared by BOTH flows (the PAA flow had
// the richer versions; the objection flow now inherits them):
//   (a) auto-verify the address via Google Places — a single same-street match
//       is adopted; ambiguity surfaces the candidates in the inline chooser
//       (instead of silently picking the first / wrong hit),
//   (b) a read-only saved-property-DB probe that auto-resolves corner lots when
//       exactly one candidate is already known,
//   (c) saved-property lookup that auto-FILLS the still-empty property-intrinsic
//       fields (block/lot/BIN/CB/borough/applicant) from the human-verified saved
//       DB — job # and address stay from the current filing, and a value the
//       extractor/user already set is left alone — and powers the "Apply saved
//       info" button, which force-overrides.
// Flow-agnostic: driven by a plain patch(partial) callback, so it works for the
// objection reducer (dispatch UPDATE_DATA) and PAA's useState alike.
export function usePropertyInfo({
  active,
  data,
  patch,
  googleMapsApiKey,
}: UsePropertyInfoArgs): UsePropertyInfoResult {
  const [savedInfo, setSavedInfo] = useState<PropertyInfoDto | null>(null);
  const [savedInfoSearching, setSavedInfoSearching] = useState(false);
  const [addressVerifying, setAddressVerifying] = useState(false);

  const verifiedAddressesRef = useRef<Set<string>>(new Set());
  const addressChoiceResolvedRef = useRef(false);

  const reset = useCallback(() => {
    verifiedAddressesRef.current = new Set();
    addressChoiceResolvedRef.current = false;
    setSavedInfo(null);
  }, []);

  const address = data?.address;
  const borough = data?.borough;
  const addressCandidates = data?.addressCandidates;

  // (a) Auto-verify via Google Places. verifiedAddressesRef (addr is added
  // before the await) dedupes; we deliberately do NOT gate on addressVerifying,
  // which could otherwise leave a new address unverified / the flag stuck true.
  useEffect(() => {
    if (!active || !googleMapsApiKey) return;
    const addr = address?.trim();
    if (!addr || verifiedAddressesRef.current.has(addr)) return;

    let cancelled = false;
    verifiedAddressesRef.current.add(addr);
    setAddressVerifying(true);
    verifyAddressViaPlaces(addr, googleMapsApiKey, borough ?? undefined)
      .then((result) => {
        if (cancelled) return;
        const decision = decideVerification(addr, result);
        if (decision === "apply") {
          // Single, same-street match — a safe canonicalization. Adopt it.
          const best = result!.best!;
          if (best.formatted === addr) return;
          verifiedAddressesRef.current.add(best.formatted);
          patch({
            address: best.formatted,
            houseNumber: best.houseNumber ?? data?.houseNumber,
            streetName: best.streetName ?? data?.streetName,
            borough: best.borough ?? data?.borough,
          });
          toast.success("Address verified via Google Maps");
        } else if (decision === "confirm") {
          // Ambiguous, or a DIFFERENT street than we asked for — never overwrite
          // silently. Surface the user's address alongside Google's candidate(s)
          // in the inline site-address chooser so they pick.
          patch({
            addressCandidates: dedupAddresses([
              addr,
              ...result!.candidates,
              ...(data?.addressCandidates ?? []),
            ]),
          });
          toast.info("More than one address matched — pick the correct one under Address.");
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setAddressVerifying(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, address, googleMapsApiKey]);

  // (b) Corner-lot resolution: probe the saved-property DB (read-only) for each
  // candidate; if EXACTLY one is already known, adopt it silently. If both or
  // neither are known, leave the inline chooser for the user. Runs once.
  useEffect(() => {
    if (!active || addressChoiceResolvedRef.current) return;
    const candidates = (addressCandidates ?? []).filter((c) => c.trim());
    if (candidates.length < 2) {
      addressChoiceResolvedRef.current = true; // nothing to choose
      return;
    }

    let cancelled = false;
    addressChoiceResolvedRef.current = true; // probe once, regardless of outcome
    (async () => {
      const known = (
        await Promise.all(
          candidates.map(async (c) => {
            try {
              return (await getPropertyInfo(c, undefined, false)) ? c : null;
            } catch {
              return null;
            }
          }),
        )
      ).filter((c): c is string => c != null);

      if (cancelled || known.length !== 1) return; // 0 or 2+ known → let user pick
      if (data?.address !== known[0]) patch({ address: known[0] });
      toast.info(`Used saved address: ${known[0]}`);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, addressCandidates]);

  // (c) Saved property info from the shared DB (keyed on normalized address).
  // Auto-applies into empty header fields; the panel offers a force-apply.
  useEffect(() => {
    if (!active) return;
    const addr = address?.trim();
    if (!addr) {
      setSavedInfo(null);
      return;
    }
    let cancelled = false;
    setSavedInfoSearching(true);
    const timer = window.setTimeout(async () => {
      try {
        const info = await getPropertyInfo(addr);
        if (cancelled) return;
        setSavedInfo(info);
        if (info) {
          // Saved info is human-verified from prior filings. Auto-apply fills ONLY
          // the still-EMPTY property-intrinsic fields (block, lot, BIN, CB,
          // borough, applicant) — it never overwrites a value the extractor found
          // or the user typed, so a later address re-resolution (Places
          // canonicalization / corner-lot pick) re-running this effect can't
          // clobber a manual correction. Job # and the address stay from the
          // CURRENT document. The "Apply saved info" button force-overrides.
          const p: Partial<PropertyHeader> = {};
          const fill = (k: keyof PropertyHeader, v: string | null | undefined) => {
            if (v && !data?.[k]) (p as Record<string, unknown>)[k] = v;
          };
          fill("cbNumber", info.cbNumber);
          fill("bin", info.bin);
          fill("applicantName", info.applicantName);
          fill("block", info.block);
          fill("lot", info.lot);
          fill("borough", info.borough);
          // Saved DB resolved the lot authoritatively — drop any extraction
          // ambiguity chooser.
          if (info.lot && (data?.lotCandidates?.length ?? 0) > 0) p.lotCandidates = [];
          if (Object.keys(p).length) {
            patch(p);
            toast.info("Applied saved property info");
          }
        }
      } catch {
        if (!cancelled) setSavedInfo(null);
      } finally {
        if (!cancelled) setSavedInfoSearching(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, address]);

  // Force-overwrite every saved field. The auto-apply effect only fills empty
  // fields, so the button is how the user overwrites values that disagree.
  const applySavedInfo = useCallback(() => {
    if (!savedInfo || !data) return;
    const p: Partial<PropertyHeader> = {};
    const changed: string[] = [];
    const set = (k: keyof PropertyHeader, v: string | null | undefined, label: string) => {
      if (v && data[k] !== v) {
        (p as Record<string, unknown>)[k] = v;
        changed.push(label);
      }
    };
    set("cbNumber", savedInfo.cbNumber, "CB");
    set("bin", savedInfo.bin, "BIN");
    set("applicantName", savedInfo.applicantName, "Applicant");
    set("block", savedInfo.block, "Block");
    set("lot", savedInfo.lot, "Lot");
    set("borough", savedInfo.borough, "Borough");
    if (!changed.length) {
      toast.info("Already matches saved property information");
      return;
    }
    patch(p);
    toast.success(`Applied saved info: ${changed.join(", ")}`);
  }, [savedInfo, data, patch]);

  return { savedInfo, savedInfoSearching, addressVerifying, applySavedInfo, reset };
}
